import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Heart, Shield, User, ExternalLink, Sword, AlertTriangle,
  X, Plus, Minus, Skull, Moon, Edit2, HeartPulse, Timer,
  Sparkles, ChevronDown, ChevronUp, Zap, Crown
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { DND_CONDITIONS } from './combatConstants';
import { toast } from 'sonner';

// Status labels
const STATUS_LABELS = {
  fr: { active: 'Vivant', unconscious: 'Inconscient', dead: 'Mort' },
  en: { active: 'Alive', unconscious: 'Unconscious', dead: 'Dead' }
};

const STATUS_COLORS = {
  active: 'bg-green-500',
  unconscious: 'bg-yellow-500',
  dead: 'bg-red-500'
};

export function CombatantCard({
  combatant,
  encounter,
  language,
  isCurrentTurn,
  showDead = true,
  onUpdateInitiative,
  onUpdateInitiativeModifier,
  onApplyDamage,
  onUpdateMaxHp,
  onToggleCondition,
  onRemove,
  onDeclareDeath,
  onUpdateStatus,
  onUpdateResources
}) {
  const [damageAmount, setDamageAmount] = useState('');
  const [maxHpAmount, setMaxHpAmount] = useState(combatant.max_hp?.toString() || '');
  const [modifierValue, setModifierValue] = useState(combatant.initiative_modifier?.toString() || '0');
  const [deathConfirmOpen, setDeathConfirmOpen] = useState(false);
  const [conditionDurations, setConditionDurations] = useState({});
  const [resourcesOpen, setResourcesOpen] = useState(false);
  
  const hpPercent = Math.max(0, (combatant.current_hp / combatant.max_hp) * 100);
  const status = combatant.status || 'active';
  const isDead = status === 'dead';
  const isUnconscious = status === 'unconscious';
  const conditions = combatant.status_effects || [];
  const trackerLabels = {
    health: language === 'fr' ? 'Santé' : 'Health',
    defense: language === 'fr' ? 'Défense' : 'Defense',
    initiative: 'Initiative',
    rating: language === 'fr' ? 'Niveau' : 'Rating',
    ...(combatant.tracker_labels || {}),
  };
  const additionalSystemStats = (combatant.system_stats || []).filter(
    field => !['health', 'defense', 'initiative', 'rating', 'reward'].includes(field.role)
  );

  // Don't show dead combatants if showDead is false
  if (isDead && !showDead) {
    return null;
  }

  const rollDice = (dice) => {
    const match = dice.match(/(\d+)?d(\d+)([+-]\d+)?/);
    if (!match) return;
    
    const count = parseInt(match[1]) || 1;
    const sides = parseInt(match[2]);
    const modifier = parseInt(match[3]) || 0;
    
    let total = modifier;
    for (let i = 0; i < count; i++) {
      total += Math.floor(Math.random() * sides) + 1;
    }
    
    setDamageAmount(String(total));
    toast.info(`${dice} = ${total}`, { description: language === 'fr' ? 'Résultat du lancer' : 'Roll result' });
  };

  const handleModifierChange = (value) => {
    setModifierValue(value);
    const num = parseInt(value) || 0;
    onUpdateInitiativeModifier(combatant.id, num);
  };

  const handleMaxHpChange = () => {
    const newMax = parseInt(maxHpAmount);
    if (!isNaN(newMax) && newMax > 0) {
      onUpdateMaxHp(combatant.id, newMax);
      toast.success(
        language === 'fr'
          ? `${trackerLabels.health} max modifié`
          : `Max ${trackerLabels.health} updated`
      );
    }
  };

  const handleConfirmDeath = () => {
    onDeclareDeath(combatant.id);
    setDeathConfirmOpen(false);
  };

  const formatModifier = (mod) => {
    const num = parseInt(mod) || 0;
    return num >= 0 ? `+${num}` : `${num}`;
  };

  return (
    <>
      <Card 
        data-testid={`combatant-card-${combatant.id}`}
        className={cn(
          "transition-all",
          isCurrentTurn && "ring-2 ring-primary shadow-lg shadow-primary/20",
          isDead && "opacity-40 grayscale",
          isUnconscious && "opacity-70 border-yellow-500"
        )}
      >
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            {/* Initiative + Modifier */}
            <div className={cn(
              "w-14 h-14 rounded-lg flex flex-col items-center justify-center shrink-0 relative",
              isCurrentTurn ? "bg-primary text-primary-foreground" : "bg-secondary",
              combatant.combatant_type === 'player' && !isCurrentTurn && "bg-blue-500/20 border-2 border-blue-500",
              combatant.combatant_type === 'npc' && !isCurrentTurn && "bg-purple-500/20 border-2 border-purple-500",
              isDead && "bg-gray-500/20 border-gray-500"
            )}>
              {encounter.status === 'preparing' ? (
                <Input
                  type="number"
                  value={combatant.initiative}
                  onChange={(e) => onUpdateInitiative(combatant.id, e.target.value)}
                  className="w-10 h-7 text-center p-0 text-lg font-bold bg-transparent border-0"
                />
              ) : (
                <span className="text-xl font-bold">{combatant.initiative}</span>
              )}
              <span className="text-[9px] opacity-70 truncate max-w-[52px]" title={trackerLabels.initiative}>
                {trackerLabels.initiative}
              </span>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              {/* Name row with modifier */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className={cn(
                  "font-semibold",
                  isDead && "line-through text-muted-foreground"
                )}>
                  {combatant.name}
                </span>
                
                {/* Initiative Modifier - Always visible */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Badge 
                      variant="outline" 
                      className="text-[10px] cursor-pointer hover:bg-secondary"
                    >
                      {formatModifier(combatant.initiative_modifier)}
                    </Badge>
                  </PopoverTrigger>
                  <PopoverContent className="w-48" align="start">
                    <div className="space-y-2">
                      <Label className="text-xs">{language === 'fr' ? 'Mod. Initiative' : 'Init. Modifier'}</Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          value={modifierValue}
                          onChange={(e) => handleModifierChange(e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Status Badge */}
                <Badge 
                  className={cn(
                    "text-[10px]",
                    STATUS_COLORS[status]
                  )}
                >
                  {status === 'unconscious' && <Moon className="h-2 w-2 mr-1" />}
                  {status === 'dead' && <Skull className="h-2 w-2 mr-1" />}
                  {STATUS_LABELS[language]?.[status] || status}
                </Badge>

                {/* Type badges */}
                {combatant.combatant_type === 'player' && (
                  <Badge variant="outline" className="text-[10px] border-blue-500 text-blue-500">
                    <User className="h-2 w-2 mr-1" />
                    {language === 'fr' ? 'Joueur' : 'Player'}
                  </Badge>
                )}
                {combatant.combatant_type === 'npc' && (
                  <Badge variant="outline" className="text-[10px] border-purple-500 text-purple-500">
                    PNJ
                  </Badge>
                )}
                {combatant.aidedd_url && (
                  <a 
                    href={combatant.aidedd_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-primary flex items-center gap-1 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    AideDD
                  </a>
                )}
                {combatant.cr && (
                  <Badge variant="secondary" className="text-[10px]">
                    {trackerLabels.rating} {combatant.cr}
                  </Badge>
                )}
              </div>
              
              {/* HP Bar */}
              <div className="flex items-center gap-2 mt-1">
                <Heart className={cn(
                  "h-4 w-4 shrink-0",
                  combatant.current_hp <= 0 ? "text-gray-500" :
                  hpPercent > 50 ? "text-green-500" :
                  hpPercent > 25 ? "text-yellow-500" : "text-red-500"
                )} />
                <div className="flex-1 relative">
                  <Progress value={Math.max(0, hpPercent)} className="h-4" />
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white drop-shadow">
                    {combatant.current_hp}/{combatant.max_hp}
                    {combatant.temp_hp > 0 && ` (+${combatant.temp_hp})`}
                    {combatant.current_hp < 0 && ` (${combatant.current_hp})`}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium" title={trackerLabels.defense}>
                    {trackerLabels.defense}: {combatant.ac}
                  </span>
                </div>
              </div>

              {additionalSystemStats.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {additionalSystemStats.map((field, index) => (
                    <Badge key={field.id || index} variant="outline" className="text-[10px]">
                      {field.label}: {String(field.value ?? '-')}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Conditions */}
              {conditions.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {conditions.map(effect => {
                    const condition = DND_CONDITIONS.find(c => c.id === effect.name);
                    if (!condition) return null;
                    const Icon = condition.icon;
                    const dur = effect.duration;
                    const isExpiring = dur != null && dur <= 2;
                    return (
                      <TooltipProvider key={effect.name}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-[10px] px-1 cursor-pointer transition-all",
                                isExpiring && dur === 1 && "animate-pulse"
                              )}
                              style={{
                                borderColor: condition.color,
                                color: condition.color,
                                backgroundColor: isExpiring ? `${condition.color}15` : undefined,
                              }}
                              onClick={() => onToggleCondition(combatant.id, effect.name)}
                            >
                              <Icon className="h-3 w-3 mr-0.5" />
                              {condition.label[language]}
                              {dur != null && (
                                <span className={cn(
                                  "ml-1 font-bold",
                                  dur === 1 && "text-red-500",
                                  dur === 2 && "text-orange-500"
                                )}>
                                  <Timer className="h-2.5 w-2.5 inline mr-0.5" />
                                  {dur}
                                </span>
                              )}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{condition.description[language]}</p>
                            {dur != null && (
                              <p className="text-xs font-semibold mt-1">
                                {language === 'fr'
                                  ? `${dur} round${dur > 1 ? 's' : ''} restant${dur > 1 ? 's' : ''}`
                                  : `${dur} round${dur > 1 ? 's' : ''} remaining`}
                              </p>
                            )}
                            {dur == null && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {language === 'fr' ? 'Permanent' : 'Permanent'}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {language === 'fr' ? 'Cliquer pour retirer' : 'Click to remove'}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 shrink-0">
              {/* Damage/Heal - always visible */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8" data-testid="damage-heal-btn">
                    <Sword className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72" align="end">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">{language === 'fr' ? 'Dégâts / Soins' : 'Damage / Heal'}</h4>
                    <div className="flex gap-1">
                      <Input
                        type="number"
                        placeholder="10"
                        value={damageAmount}
                        onChange={(e) => setDamageAmount(e.target.value)}
                        className="flex-1"
                      />
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => { onApplyDamage(combatant.id, damageAmount, false); setDamageAmount(''); }}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => { onApplyDamage(combatant.id, damageAmount, true); setDamageAmount(''); }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {['1d4', '1d6', '1d8', '1d10', '1d12', '2d6'].map(dice => (
                        <Button 
                          key={dice} 
                          variant="outline" 
                          size="sm" 
                          className="text-xs h-7"
                          onClick={() => rollDice(dice)}
                        >
                          {dice}
                        </Button>
                      ))}
                    </div>
                    
                    {/* Max HP modification for monsters */}
                    {combatant.combatant_type === 'monster' && (
                      <div className="border-t pt-3 mt-3">
                        <Label className="text-xs">
                          {language === 'fr' ? `Modifier ${trackerLabels.health} max` : `Modify max ${trackerLabels.health}`}
                        </Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            value={maxHpAmount}
                            onChange={(e) => setMaxHpAmount(e.target.value)}
                            className="flex-1"
                          />
                          <Button size="sm" onClick={handleMaxHpChange}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Conditions */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8">
                    <AlertTriangle className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="end">
                  <h4 className="font-semibold mb-2">
                    {language === 'fr' ? 'Conditions D&D 5e' : 'D&D 5e Conditions'}
                  </h4>
                  <div className="grid grid-cols-1 gap-1 max-h-64 overflow-y-auto">
                    {DND_CONDITIONS.map(condition => {
                      const Icon = condition.icon;
                      const hasCondition = conditions.some(e => e.name === condition.id);
                      const durVal = conditionDurations[condition.id] ?? '';
                      return (
                        <div key={condition.id} className="flex items-center gap-1">
                          <Button
                            variant={hasCondition ? "default" : "outline"}
                            size="sm"
                            className="justify-start text-xs h-8 flex-1"
                            style={hasCondition ? { backgroundColor: condition.color } : {}}
                            onClick={() => {
                              const dur = durVal !== '' ? parseInt(durVal) : null;
                              onToggleCondition(combatant.id, condition.id, dur);
                            }}
                          >
                            <Icon className="h-3 w-3 mr-1" />
                            {condition.label[language]}
                          </Button>
                          {!hasCondition && (
                            <Input
                              type="number"
                              min="1"
                              max="99"
                              placeholder={language === 'fr' ? 'Rds' : 'Rds'}
                              className="w-16 h-8 text-xs"
                              value={durVal}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setConditionDurations(prev => ({
                                ...prev, [condition.id]: e.target.value
                              }))}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    {language === 'fr'
                      ? 'Laisser vide = permanent. Saisir un nombre = rounds.'
                      : 'Leave empty = permanent. Enter a number = rounds.'}
                  </p>
                </PopoverContent>
              </Popover>

              {/* Declare Death (players only, when unconscious or at 0 HP) */}
              {combatant.combatant_type === 'player' && (isUnconscious || combatant.current_hp <= 0) && !isDead && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="destructive" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => setDeathConfirmOpen(true)}
                      >
                        <Skull className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {language === 'fr' ? 'Déclarer mort définitive' : 'Declare definitive death'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {/* Remove - only in preparing phase */}
              {encounter.status === 'preparing' && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8"
                  onClick={() => onRemove(combatant.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Spell Slots & Resources (player combatants) */}
          {combatant.combatant_type === 'player' && (combatant.spell_slots || combatant.resources?.length > 0) && (
            <div className="mt-2 border-t border-border pt-2">
              <button
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground w-full"
                onClick={() => setResourcesOpen(!resourcesOpen)}
              >
                <Sparkles className="h-3 w-3" />
                {language === 'fr' ? 'Sorts & Ressources' : 'Spells & Resources'}
                {resourcesOpen ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
              </button>
              {resourcesOpen && (
                <div className="mt-2 space-y-2">
                  {/* Spell Slots */}
                  {combatant.spell_slots && Object.keys(combatant.spell_slots).length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">
                        {language === 'fr' ? 'Emplacements de sorts' : 'Spell Slots'}
                      </p>
                      <div className="grid grid-cols-3 gap-1">
                        {Object.entries(combatant.spell_slots)
                          .sort(([a], [b]) => parseInt(a) - parseInt(b))
                          .map(([level, slot]) => {
                            const max = slot.max || 0;
                            const used = slot.used || 0;
                            const remaining = max - used;
                            return (
                              <div key={level} className="flex items-center gap-1 text-[10px]">
                                <span className="text-muted-foreground w-6">Nv{level}</span>
                                <div className="flex gap-0.5">
                                  {Array.from({ length: max }).map((_, i) => (
                                    <button
                                      key={i}
                                      className={cn(
                                        "w-3 h-3 rounded-full border transition-colors",
                                        i < remaining
                                          ? "bg-primary border-primary"
                                          : "bg-muted border-muted-foreground/30"
                                      )}
                                      onClick={() => {
                                        const newSlots = { ...combatant.spell_slots };
                                        if (i < remaining) {
                                          newSlots[level] = { ...slot, used: used + 1 };
                                        } else {
                                          newSlots[level] = { ...slot, used: Math.max(0, used - 1) };
                                        }
                                        onUpdateResources?.(combatant.id, { spell_slots: newSlots });
                                      }}
                                    />
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Custom Resources */}
                  {combatant.resources?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">
                        {language === 'fr' ? 'Ressources' : 'Resources'}
                      </p>
                      <div className="space-y-1">
                        {combatant.resources.map((res, idx) => {
                          const max = res.max || 0;
                          const used = res.used || 0;
                          const remaining = max - used;
                          return (
                            <div key={idx} className="flex items-center gap-2 text-[10px]">
                              <Zap className="h-3 w-3 text-yellow-500 shrink-0" />
                              <span className="text-muted-foreground truncate w-16">{res.name}</span>
                              <div className="flex gap-0.5">
                                {Array.from({ length: max }).map((_, i) => (
                                  <button
                                    key={i}
                                    className={cn(
                                      "w-3 h-3 rounded-sm border transition-colors",
                                      i < remaining
                                        ? "bg-yellow-500 border-yellow-500"
                                        : "bg-muted border-muted-foreground/30"
                                    )}
                                    onClick={() => {
                                      const newResources = [...combatant.resources];
                                      if (i < remaining) {
                                        newResources[idx] = { ...res, used: used + 1 };
                                      } else {
                                        newResources[idx] = { ...res, used: Math.max(0, used - 1) };
                                      }
                                      onUpdateResources?.(combatant.id, { resources: newResources });
                                    }}
                                  />
                                ))}
                              </div>
                              <span className="text-muted-foreground">{remaining}/{max}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Legendary & Lair Actions (monster combatants) */}
          {combatant.combatant_type === 'monster' && (combatant.legendary_actions_max > 0 || combatant.has_lair_actions) && (
            <div className="mt-2 border-t border-border pt-2 space-y-2">
              {/* Legendary Actions */}
              {combatant.legendary_actions_max > 0 && (
                <div className="flex items-center gap-2">
                  <Crown className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase">
                    {language === 'fr' ? 'Légendaires' : 'Legendary'}
                  </span>
                  <div className="flex gap-0.5 ml-auto">
                    {Array.from({ length: combatant.legendary_actions_max }).map((_, i) => {
                      const used = combatant.legendary_actions_used || 0;
                      const remaining = combatant.legendary_actions_max - used;
                      return (
                        <button
                          key={i}
                          className={cn(
                            "w-4 h-4 rounded-full border-2 transition-colors",
                            i < remaining
                              ? "bg-amber-500 border-amber-500"
                              : "bg-muted border-muted-foreground/30"
                          )}
                          onClick={() => {
                            const newUsed = i < remaining ? used + 1 : Math.max(0, used - 1);
                            onUpdateResources?.(combatant.id, { legendary_actions_used: newUsed });
                          }}
                        />
                      );
                    })}
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    {combatant.legendary_actions_max - (combatant.legendary_actions_used || 0)}/{combatant.legendary_actions_max}
                  </span>
                </div>
              )}

              {/* Lair Actions */}
              {combatant.has_lair_actions && (
                <div className="flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5 text-purple-500 shrink-0" />
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase">
                    {language === 'fr' ? 'Repaire' : 'Lair'}
                  </span>
                  <button
                    className={cn(
                      "ml-auto px-2 py-0.5 rounded text-[10px] font-medium border transition-colors",
                      combatant.lair_action_used
                        ? "bg-muted text-muted-foreground border-muted-foreground/30"
                        : "bg-purple-500/20 text-purple-400 border-purple-500"
                    )}
                    onClick={() => {
                      onUpdateResources?.(combatant.id, { lair_action_used: !combatant.lair_action_used });
                    }}
                  >
                    {combatant.lair_action_used
                      ? (language === 'fr' ? 'Utilisée' : 'Used')
                      : (language === 'fr' ? 'Disponible' : 'Available')}
                  </button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Death Confirmation Dialog */}
      <Dialog open={deathConfirmOpen} onOpenChange={setDeathConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Skull className="h-5 w-5" />
              {language === 'fr' ? 'Confirmer la mort' : 'Confirm death'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? `Êtes-vous sûr de vouloir déclarer ${combatant.name} définitivement mort(e) ? Cette action le/la retirera du combat.`
                : `Are you sure you want to declare ${combatant.name} definitively dead? This will remove them from combat.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeathConfirmOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleConfirmDeath}>
              <Skull className="mr-2 h-4 w-4" />
              {language === 'fr' ? 'Confirmer la mort' : 'Confirm death'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
